import { ActionIcon, Badge, Group, Paper, Stack, Table, Text, Tooltip } from '@mantine/core';
import { IconPencil, IconTrash } from '@tabler/icons-react';

import { providerLabel, type ProviderAgreement } from '@/types/provider-cost';
import { formatMoney } from '@/utils';
import { formatRate, todayIso } from './format';

export interface ProviderAgreementListProps {
  agreements: ProviderAgreement[];
  /** Outlined, and scrolled to, when opened from one of its rates. */
  highlightedId?: string | null;
  onEdit: (agreement: ProviderAgreement) => void;
  onDelete: (agreement: ProviderAgreement) => void;
}

type Period = 'current' | 'upcoming' | 'ended';

function periodOf(agreement: ProviderAgreement, today: string): Period {
  if (agreement.endsOn < today) {
    return 'ended';
  }
  return agreement.startsOn > today ? 'upcoming' : 'current';
}

const PERIOD_BADGE: Record<Period, { label: string; color: string }> = {
  current: { label: 'Current', color: 'green' },
  upcoming: { label: 'Upcoming', color: 'blue' },
  ended: { label: 'Ended', color: 'gray' },
};

/** The prepaid agreements, newest first within each provider, each with the
 * rates its lines put on the rate card. */
export function ProviderAgreementList({
  agreements,
  highlightedId = null,
  onEdit,
  onDelete,
}: Readonly<ProviderAgreementListProps>) {
  if (agreements.length === 0) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="lg">
        No agreements. Record a prepaid pool here and its rates are added to the rate card.
      </Text>
    );
  }

  const today = todayIso();
  return (
    <Stack>
      {agreements.map((agreement) => {
        const period = PERIOD_BADGE[periodOf(agreement, today)];
        return (
          <Paper
            key={agreement.id}
            withBorder
            p="md"
            id={`agreement-${agreement.id}`}
            data-testid={`agreement-${agreement.id}`}
            style={
              agreement.id === highlightedId
                ? { borderColor: 'var(--mantine-primary-color-filled)' }
                : undefined
            }
          >
            <Group justify="space-between" align="flex-start" mb="sm">
              <Stack gap={2}>
                <Group gap="xs">
                  <Text fw={600}>{providerLabel(agreement.provider)}</Text>
                  <Badge size="sm" variant="light" color={period.color}>
                    {period.label}
                  </Badge>
                  <Badge size="sm" variant="outline" color="gray">
                    {agreement.sharedPool ? 'Shared pool' : 'Priced per line'}
                  </Badge>
                </Group>
                <Text size="sm" c="dimmed">
                  {agreement.startsOn} to {agreement.endsOn} ·{' '}
                  {formatMoney(agreement.price, agreement.currency)}
                </Text>
                {agreement.notes ? <Text size="sm">{agreement.notes}</Text> : null}
              </Stack>
              <Group gap={4}>
                <Tooltip label="Edit">
                  <ActionIcon
                    variant="subtle"
                    aria-label="Edit agreement"
                    onClick={() => onEdit(agreement)}
                  >
                    <IconPencil size={16} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Delete, with its rates">
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    aria-label="Delete agreement"
                    onClick={() => onDelete(agreement)}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Group>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Service</Table.Th>
                  <Table.Th>Unit</Table.Th>
                  <Table.Th ta="right">Allowance</Table.Th>
                  {agreement.sharedPool ? null : <Table.Th ta="right">Share of price</Table.Th>}
                  <Table.Th ta="right">Overage per unit</Table.Th>
                  <Table.Th ta="right">Rate</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {agreement.lines.map((line) => (
                  <Table.Tr key={line.id}>
                    <Table.Td>
                      <Text size="sm" ff="monospace">
                        {line.service}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" ff="monospace">
                        {line.unitKey}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right">{line.allowance.toLocaleString('en-GB')}</Table.Td>
                    {agreement.sharedPool ? null : (
                      <Table.Td ta="right">
                        {formatMoney(line.priceShare, agreement.currency)}
                      </Table.Td>
                    )}
                    <Table.Td ta="right">
                      {line.overageRate === null
                        ? '—'
                        : formatRate(line.overageRate, agreement.currency)}
                    </Table.Td>
                    <Table.Td ta="right" fw={500}>
                      {formatRate(line.costPerUnit, agreement.currency)}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        );
      })}
    </Stack>
  );
}
